import { invalidateAnswerCaches, trackAnswerWrite } from '@/api/answerWrites';
import { ApiError, isGuest, request, retryable } from '@/api/client';
import { reportWarning } from '@/sentry';
import { readStored, writeStored } from '@/storage';

export interface AttemptResult {
  id: string;
  question_id: string;
  selected_answer_index: number;
  is_correct: boolean;
  correct_answer_index: number;
}

export interface BatchResult {
  total: number;
  correct: number;
  score_pct: number;
  results: AttemptResult[];
}

export interface AttemptBody {
  question_id: string;
  selected_answer_index: number;
  mode?: 'practice' | 'exam';
}

const OUTBOX_KEY = 'attempt_outbox';

const OUTBOX_MAX = 100;

function readOutbox(): AttemptBody[] {
  try {
    const raw: unknown = JSON.parse(readStored(OUTBOX_KEY) ?? '[]');
    if (!Array.isArray(raw)) return [];
    return raw.filter(
      (x): x is AttemptBody =>
        typeof x === 'object' &&
        x !== null &&
        typeof (x as AttemptBody).question_id === 'string' &&
        typeof (x as AttemptBody).selected_answer_index === 'number',
    );
  } catch {
    return [];
  }
}

let overflowReported = false;

export function writeOutbox(items: AttemptBody[]): void {
  const dropped = items.length - OUTBOX_MAX;
  if (dropped > 0 && !overflowReported) {
    overflowReported = true;
    reportWarning('Attempt outbox full, oldest answers dropped', { dropped, kept: OUTBOX_MAX });
  }
  writeStored(OUTBOX_KEY, JSON.stringify(items.slice(-OUTBOX_MAX)));
}

let flushing = false;

export async function flushAttempts(): Promise<void> {
  if (flushing) return;
  const queued = readOutbox();
  if (queued.length === 0 || isGuest()) return;
  flushing = true;
  try {
    await postAttemptsBatch(queued);
    writeOutbox(readOutbox().slice(queued.length));
  } catch (e) {
    if (!retryable(e)) {
      writeOutbox(readOutbox().slice(queued.length));
      reportWarning('Attempt outbox gave up on answers the server refused', {
        dropped: queued.length,
        status: e instanceof ApiError ? e.status : null,
      });
    }
  } finally {
    flushing = false;
  }
}

export function postAttempt(body: AttemptBody): Promise<AttemptResult> {
  if (isGuest()) return Promise.reject(new ApiError(401, 'guest'));
  invalidateAnswerCaches();
  return trackAnswerWrite(
    request<AttemptResult>('/attempts', { method: 'POST', body: JSON.stringify(body) }).then(
      (result) => {
        void flushAttempts();
        return result;
      },
      (e: unknown) => {
        if (retryable(e)) {
          writeOutbox([...readOutbox(), body]);
        }
        throw e;
      },
    ),
  );
}

export function postAttemptsBatch(items: AttemptBody[]): Promise<BatchResult> {
  invalidateAnswerCaches();
  return trackAnswerWrite(
    request<BatchResult>('/attempts/batch', {
      method: 'POST',
      body: JSON.stringify({ items }),
    }),
  );
}
